import { currencySuffix, formatMoney } from '../../../shared/format/money.ts';
import { completeYear, formatRange, periodResult } from '../../../shared/format/period.ts';
import React, { useState, useRef, useMemo } from 'react';
import toast from 'react-hot-toast';
import { Upload, X, ChevronDown, CheckCircle, XCircle, Shield, AlertTriangle, ArrowUpDown, Search, Library } from 'lucide-react';
import axiosInstance from '../../../api/axios';
import { resolve403ErrorMessage } from '../../../request/getApiErrorMessage.ts';
import {
  POPULATION_ACCEPT, POPULATION_FORMATS_LABEL, UNSUPPORTED_POPULATION_FILE_MESSAGE, isSupportedPopulationFile,
} from '../../../constants/populationFormats.ts';
import { populationPrompt } from '../../attribute-result/populationPrompt';
import { bandSampleSize } from '../../attribute-result/sampleSize';
import { selectedRowData } from '../populationRows';
import InputChecksBanner from '../InputChecksBanner';
import { LibraryPicker } from '../../../features/evidence-library/index.ts';
import { linkLibraryFiles, parseLibraryPopulation } from '../../../request/evidence-library/evidenceLibraryRequests.ts';

/**
 * PopulationStep — Step 2 of Test Run Wizard.
 *
 * testingMethod === 'attribute': shows the 4-section attribute IPE flow.
 * Otherwise: shows the existing holistic flow (non-event or event-based).
 *
 * onComplete({ populationData, totalPopulation, ipeValidated, samplingMethod })
 */
export default function PopulationStep({ periodData, control, controlUuid, runId, testType, testingMethod, initialPopulation, initialSamplingMethod, onComplete, onBack }) {
  const isEventBased = periodData?.isEventBased || false;
  const periods      = periodData?.periods || [];

  if (testingMethod === 'attribute') {
    return (
      <AttributePopulation
        control={control}
        controlUuid={controlUuid}
        runId={runId}
        testType={testType}
        testingPeriod={{
          start: periodData?.testing_period_start || periods[0]?.start || null,
          end: periodData?.testing_period_end || periods[periods.length - 1]?.end || null,
        }}
        initialPopulation={initialPopulation}
        initialSamplingMethod={initialSamplingMethod}
        onComplete={onComplete}
        onBack={onBack}
      />
    );
  }

  if (!isEventBased) {
    return (
      <NonEventPopulation
        periods={periods}
        control={control}
        storeCtx={{ controlUuid, runId, periodStart: periodData?.testing_period_start || periods[0]?.start || null, periodEnd: periodData?.testing_period_end || periods[periods.length - 1]?.end || null }}
        testType={testType}
        initialPopulation={initialPopulation}
        initialSamplingMethod={initialSamplingMethod}
        onComplete={onComplete}
        onBack={onBack}
      />
    );
  }

  return (
    <EventBasedPopulation
      control={control}
      storeCtx={{ controlUuid, runId, periodStart: periodData?.testing_period_start || null, periodEnd: periodData?.testing_period_end || null }}
      initialPopulation={initialPopulation}
      initialSamplingMethod={initialSamplingMethod}
      onComplete={onComplete}
      onBack={onBack}
    />
  );
}

/**
 * AICPA attribute-sampling recommendation by control frequency.
 * Returns {min, max, rationale}. For TOD a single walkthrough suffices.
 */
function recommendedSampleSize(frequency, testType) {
  if (testType === 'tod') return { min: 1, max: 1, rationale: 'Test of Design — single walkthrough' };
  const f = (frequency || '').toLowerCase();
  const table = {
    'annual':       { min: 1,  max: 1,  rationale: 'Annual control — 1' },
    'semi-annual':  { min: 1,  max: 2,  rationale: 'Semi-annual — 1–2' },
    'quarterly':    { min: 2,  max: 2,  rationale: 'Quarterly — 2' },
    'monthly':      { min: 2,  max: 5,  rationale: 'Monthly — 2–5' },
    'semi-monthly': { min: 3,  max: 8,  rationale: 'Semi-monthly — 3–8' },
    'bi-weekly':    { min: 5,  max: 15, rationale: 'Bi-weekly — 5–15' },
    'weekly':       { min: 5,  max: 15, rationale: 'Weekly — 5–15' },
    'daily':        { min: 25, max: 40, rationale: 'Daily — 25–40' },
  };
  return table[f] || { min: 25, max: 40, rationale: 'Default — 25–40' };
}

/** Date part (YYYY-MM-DD) of a period bound, or undefined. */
function dayOf(v) {
  return v ? String(v).split('T')[0] : undefined;
}

/* ---- Shared: upload population file → parse → pick samples ----
 * storeCtx { controlUuid, runId, periodStart, periodEnd }: when it has a control, every population file read here is
 * also saved to the evidence library as this control's population (and can be picked from the library instead). */
function usePopulationFileUpload(initialPopulation, storeCtx = {}) {
  const saved = initialPopulation || {};
  const [phase, setPhase] = useState(
    saved.selected_samples?.length ? 'sampled' : 'upload'
  );
  const [parseResult, setParseResult] = useState(
    saved.total_count ? {
      total_count: saved.total_count,
      identifier_column: saved.file_name || saved.identifier_column || 'ID',
      items: saved.items || saved.selected_samples || [],
      // Each saved record's row (date, amount, ...): saving the samples again keeps it (see savedItemsDetail).
      items_detail: savedItemsDetail(saved.items),
      available_columns: null,
    } : null
  );
  const [samplingPct, setSamplingPct] = useState(saved.sampling_pct || 10);
  const [selectedSamples, setSelectedSamples] = useState(saved.selected_samples || []);
  // 'random' | 'systematic' | 'haphazard' | 'judgmental'
  const [selectionMode, setSelectionMode] = useState('random');
  const [manualChecked, setManualChecked] = useState(
    new Set(saved.selected_samples || [])
  );
  const [systematicSize, setSystematicSize] = useState(null);
  const [error, setError] = useState('');
  const fileRef = useRef(null);
  // stored file bytes so we can re-parse with a different column without re-upload
  const storedFileRef = useRef(null);
  const storedFileNameRef = useRef('upload');
  const [reParsingColumn, setReParsingColumn] = useState(false);
  // AI-read files (images, scans) must be checked by the auditor before sampling.
  const [reviewConfirmed, setReviewConfirmed] = useState(false);
  // The library copy of the file being sampled: set after an upload is stored, or when the file came from the library.
  const [storedUploadId, setStoredUploadId] = useState(saved.population_file_id ?? null);
  const [storeError, setStoreError] = useState('');
  const [libraryOpen, setLibraryOpen] = useState(false);
  // A library file re-parses from its stored copy (no browser bytes to send again).
  const libraryFileIdRef = useRef(null);
  const canUseLibrary = Boolean(storeCtx.controlUuid);

  // Each file read here gets a token: a save that finishes after the user moved on to another file is ignored, and
  // the confirm step waits for the current file's save (waitForStoredUpload) before recording its id.
  const storeTokenRef = useRef(0);
  const storePromiseRef = useRef(Promise.resolve());
  const storedIdRef = useRef(saved.population_file_id ?? null);
  const setStored = (id) => { storedIdRef.current = id; setStoredUploadId(id); };
  const waitForStoredUpload = async () => {
    await storePromiseRef.current;
    return storedIdRef.current;
  };

  const storeInLibrary = (file) => {
    const token = ++storeTokenRef.current;
    setStored(null);
    storePromiseRef.current = saveToLibrary(file, token);
    return storePromiseRef.current;
  };

  const saveToLibrary = async (file, token) => {
    if (!storeCtx.controlUuid) return;
    setStoreError('');
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('file_category', 'population');
      if (storeCtx.runId) form.append('test_run_id', storeCtx.runId);
      if (dayOf(storeCtx.periodStart) && dayOf(storeCtx.periodEnd)) {
        form.append('testing_period_start', dayOf(storeCtx.periodStart));
        form.append('testing_period_end', dayOf(storeCtx.periodEnd));
      }
      const resp = await axiosInstance.post(`/api/sox-controls/${storeCtx.controlUuid}/evidence/upload`, form,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      if (token === storeTokenRef.current) setStored(resp.data?.control_upload_id ?? null);
    } catch (err) {
      if (token !== storeTokenRef.current) return;
      // Sampling can go on; the file just is not in the library.
      setStoreError(resolve403ErrorMessage(err, err.response?.data?.error || 'The file was read but could not be saved to the evidence library.'));
    }
  };

  const pickFromLibrary = async (picks) => {
    const libFile = picks[0]?.file;
    if (!libFile) return;
    setPhase('parsing');
    setError('');
    setReviewConfirmed(false);
    try {
      const data = await parseLibraryPopulation(libFile.id);
      storedFileRef.current = null;
      libraryFileIdRef.current = libFile.id;
      storedFileNameRef.current = libFile.filename;
      setParseResult(data);
      setManualChecked(new Set());
      setPhase('sampling');
      storeTokenRef.current += 1;
      storePromiseRef.current = Promise.resolve();
      setStored(libFile.id);
      setLibraryOpen(false);
      linkLibraryFiles({
        sox_control_id: storeCtx.controlUuid,
        test_run_id: storeCtx.runId || undefined,
        items: [{
          control_upload_id: libFile.id,
          file_category: 'population',
          testing_period_start: dayOf(storeCtx.periodStart),
          testing_period_end: dayOf(storeCtx.periodEnd),
        }],
      }).then((results) => {
        const failed = (results || []).find((r) => r.status === 'error');
        if (failed) setStoreError(`The file was read but could not be linked to this control: ${failed.error}`);
      }).catch(() => setStoreError('The file was read but could not be linked to this control.'));
    } catch (err) {
      setError(resolve403ErrorMessage(err, err.response?.data?.error || 'Failed to parse population file'));
      setPhase('upload');
      throw err;
    }
  };

  const doParse = async (file, identifierColumn = null) => {
    const form = new FormData();
    form.append('file', file);
    const headers = { 'Content-Type': 'multipart/form-data' };
    if (identifierColumn) headers['X-Identifier-Column'] = identifierColumn;
    const resp = await axiosInstance.post('/api/test-runs/population/parse', form, { headers });
    return resp.data;
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isSupportedPopulationFile(file)) {
      setError(UNSUPPORTED_POPULATION_FILE_MESSAGE);
      e.target.value = '';
      return;
    }
    setReviewConfirmed(false);
    storedFileRef.current = file;
    libraryFileIdRef.current = null;
    storedFileNameRef.current = file.name;
    setPhase('parsing');
    setError('');
    try {
      const data = await doParse(file);
      setParseResult(data);
      setManualChecked(new Set());
      setPhase('sampling');
      storeInLibrary(file);
    } catch (err) {
      setError(resolve403ErrorMessage(err, err.response?.data?.error || 'Failed to parse population file'));
      setPhase('upload');
    }
  };

  const handleColumnChange = async (col) => {
    if (!storedFileRef.current && !libraryFileIdRef.current) return;
    setReParsingColumn(true);
    setError('');
    try {
      const data = storedFileRef.current
        ? await doParse(storedFileRef.current, col)
        : await parseLibraryPopulation(libraryFileIdRef.current, col);
      setParseResult(data);
      setManualChecked(new Set());
      setSelectedSamples([]);
      setPhase('sampling');
    } catch (err) {
      setError(resolve403ErrorMessage(err, err.response?.data?.error || 'Failed to re-parse with selected column'));
    } finally {
      setReParsingColumn(false);
    }
  };

  const pickRandom = (items, size) => {
    const shuffled = [...items].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, size);
  };

  const handleSelectSamples = () => {
    if (!parseResult?.items) return;
    const size = Math.max(1, Math.round((parseResult.total_count * samplingPct) / 100));
    const picked = pickRandom(parseResult.items, size);
    setSelectedSamples(picked);
    setManualChecked(new Set(picked));
    setPhase('sampled');
  };

  const handleSelectRecommended = () => {
    if (!parseResult?.items) return;
    const rec = bandSampleSize(parseResult.total_count);
    const size = Math.max(1, rec.size || 1);
    const picked = pickRandom(parseResult.items, size);
    setSelectedSamples(picked);
    setManualChecked(new Set(picked));
    setPhase('sampled');
  };

  // Systematic sampling: pick every Nth item, starting at a random point
  // within the first interval. N = floor(total / target).
  const handleSelectSystematic = (targetSize) => {
    if (!parseResult?.items) return;
    const items = parseResult.items;
    const n = Math.max(1, Math.min(targetSize, items.length));
    const interval = Math.max(1, Math.floor(items.length / n));
    const start = Math.floor(Math.random() * interval);
    const picked = [];
    for (let i = start; i < items.length && picked.length < n; i += interval) {
      picked.push(items[i]);
    }
    setSelectedSamples(picked);
    setManualChecked(new Set(picked));
    setPhase('sampled');
  };

  const confirmManual = () => {
    const picked = parseResult?.items.filter(item => manualChecked.has(item)) || [];
    setSelectedSamples(picked);
    setPhase('sampled');
  };

  const toggleManualItem = (item) => {
    setManualChecked(prev => {
      const next = new Set(prev);
      next.has(item) ? next.delete(item) : next.add(item);
      return next;
    });
  };

  const selectAllManual = () => {
    setManualChecked(new Set(parseResult?.items || []));
  };

  const deselectAllManual = () => {
    setManualChecked(new Set());
  };

  const removeSample = (item) => {
    setSelectedSamples(prev => prev.filter(s => s !== item));
  };

  const resetToUpload = () => {
    setParseResult(null);
    setSelectedSamples([]);
    setManualChecked(new Set());
    storedFileRef.current = null;
    libraryFileIdRef.current = null;
    storeTokenRef.current += 1;
    storePromiseRef.current = Promise.resolve();
    setStored(null);
    setStoreError('');
    setReviewConfirmed(false);
    setPhase('upload');
  };

  /** Seed from a file already stored (Section A of the attribute flow), so a column change can re-parse it. */
  const setLibraryFileId = (id) => { libraryFileIdRef.current = id ?? null; if (id) setStored(id); };

  return {
    reviewConfirmed, setReviewConfirmed,
    phase, setPhase, parseResult, setParseResult, samplingPct, setSamplingPct, selectedSamples,
    selectionMode, setSelectionMode, manualChecked, setManualChecked, toggleManualItem, confirmManual,
    selectAllManual, deselectAllManual, removeSample,
    error, fileRef, handleFileUpload, handleSelectSamples, handleSelectRecommended,
    resetToUpload, handleColumnChange, reParsingColumn,
    systematicSize, setSystematicSize, handleSelectSystematic,
    storedUploadId, waitForStoredUpload, storeError, canUseLibrary, libraryOpen, setLibraryOpen, pickFromLibrary, setLibraryFileId, storeCtx,
  };
}

const PAGE_SIZE = 50;

/** items_detail (as the parser returns it) rebuilt from a saved population's items and their row_data. */
export function savedItemsDetail(items) {
  return (items || [])
    .filter((it) => it && typeof it === 'object' && it.row_data && typeof it.row_data === 'object')
    .map((it) => {
      const { _row_number: rowNumber, _page_number: pageNumber, ...fields } = it.row_data;
      return { ref: String(it.record_ref ?? '').trim(), fields, row_number: rowNumber ?? null, page_number: pageNumber ?? null };
    })
    .filter((d) => d.ref);
}

/** What the parser said about the file, each message once: the ones to act on shown, the rest under "Details".
 *  Older results have only `notes`, shown as they are. */
export function ParseMessages({ result, tone = 'blue' }) {
  const [open, setOpen] = useState(false);
  const messages = result?.messages?.length
    ? result.messages
    : (result?.notes ? [{ level: 'attention', text: result.notes }] : []);
  const attention = messages.filter((m) => m.level === 'attention');
  const info = messages.filter((m) => m.level !== 'attention');
  if (!messages.length) return null;
  const text = tone === 'amber' ? 'text-amber-800' : 'text-blue-700';
  return (
    <div className="space-y-0.5" data-testid="parse-messages">
      {attention.map((m) => <p key={m.text} className={`text-xs ${text}`}>{m.text}</p>)}
      {info.length > 0 && (
        <div>
          <button type="button" onClick={() => setOpen((o) => !o)} className="text-[11px] text-gray-500 hover:text-gray-700 underline">
            {open ? 'Hide details' : `Details (${info.length})`}
          </button>
          {open && info.map((m) => <p key={m.text} className="text-[11px] text-gray-500">{m.text}</p>)}
        </div>
      )}
    </div>
  );
}

function fmt_amount(val, currency) {
  return formatMoney(val, currency, { decimals: 0 });
}

function fmt_date(val) {
  if (!val) return '—';
  const s = String(val);
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : s.split('T')[0] || s;
}

/**
 * Rich population table — shows items_detail with sort, search, and pagination.
 * Used for haphazard/judgmental selection and for the sampled review view.
 */
function PopulationTable({ parseResult, manualChecked, setManualChecked, toggleManualItem, selectAllManual, deselectAllManual, confirmManual, selectionMode, readOnly }) {
  const [search, setSearch]   = useState('');
  const [sortCol, setSortCol] = useState(null);   // column name or 'ref'
  const [sortAsc, setSortAsc] = useState(true);
  const [page, setPage]       = useState(0);

  // Judgmental mode: default-sort by amount desc so high-value items are on top.
  // Read parseResult inside the effect (stable by the time selectionMode changes).
  React.useEffect(() => {
    const amountCol = (parseResult?.key_columns || {}).amount || null;
    if (selectionMode === 'judgmental' && amountCol) {
      setSortCol(amountCol);
      setSortAsc(false);
      setPage(0);
    } else if (selectionMode === 'haphazard') {
      setSortCol(null);
      setSortAsc(true);
      setPage(0);
    }
  }, [selectionMode]); // eslint-disable-line

  const detail   = parseResult.items_detail || [];
  const keyCol   = parseResult.key_columns  || {};
  const amountCol    = keyCol.amount;
  const vendorCol    = keyCol.vendor;
  const dateCol      = keyCol.date;
  const descCol      = keyCol.description;
  const hasApprover  = !!keyCol.approver;

  // Build display columns in order: ref, vendor, amount, date, description
  const displayCols = [
    { key: 'ref', label: parseResult.identifier_column || 'ID' },
    vendorCol  && { key: vendorCol,  label: vendorCol  },
    amountCol  && { key: amountCol,  label: amountCol  },
    dateCol    && { key: dateCol,    label: dateCol    },
    descCol    && { key: descCol,    label: descCol    },
  ].filter(Boolean);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = q
      ? detail.filter(r =>
          r.ref.toLowerCase().includes(q) ||
          Object.values(r.fields).some(v => v.toLowerCase().includes(q))
        )
      : detail;

    if (sortCol) {
      rows = [...rows].sort((a, b) => {
        const va = sortCol === 'ref' ? a.ref : (a.fields[sortCol] || '');
        const vb = sortCol === 'ref' ? b.ref : (b.fields[sortCol] || '');
        // numeric sort for amount column
        if (sortCol === amountCol) {
          return sortAsc ? parseFloat(va || 0) - parseFloat(vb || 0) : parseFloat(vb || 0) - parseFloat(va || 0);
        }
        return sortAsc ? va.localeCompare(vb) : vb.localeCompare(va);
      });
    }
    return rows;
  }, [detail, search, sortCol, sortAsc, amountCol]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageRows   = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const toggleSort = (col) => {
    if (sortCol === col) setSortAsc(a => !a);
    else { setSortCol(col); setSortAsc(col === amountCol ? false : true); }
    setPage(0);
  };

  const selectTopByAmount = (n) => {
    if (!amountCol) return;
    const sorted = [...detail].sort((a, b) => parseFloat(b.fields[amountCol] || 0) - parseFloat(a.fields[amountCol] || 0));
    // Single atomic update — avoids stale-closure bug where the guard
    // `manualChecked.has(r.ref)` would read pre-deselect state and skip
    // items that happened to be already checked.
    setManualChecked(new Set(sorted.slice(0, n).map(r => r.ref)));
    // Sort table by amount desc so selected rows appear at the top
    setSortCol(amountCol);
    setSortAsc(false);
    setPage(0);
  };

  const formatCell = (col, val) => {
    if (!val) return <span className="text-gray-300">—</span>;
    if (col === amountCol) return <span className={parseFloat(val) >= 100000 ? 'font-medium text-red-600' : parseFloat(val) >= 10000 ? 'text-amber-600' : 'text-gray-700'}>{fmt_amount(val, parseResult?.currency)}</span>;
    if (col === dateCol)   return <span className="text-gray-600 text-xs">{fmt_date(val)}</span>;
    if (col === descCol)   return <span className="text-gray-500 text-xs truncate block max-w-[200px]" title={val}>{val}</span>;
    return <span className="text-gray-800">{val}</span>;
  };

  return (
    <div className="space-y-2">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            placeholder="Search vendor, doc #, memo…"
            className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
        </div>
        {!readOnly && (
          <>
            <button type="button" onClick={selectAllManual} className="text-xs text-blue-600 hover:underline whitespace-nowrap">Select all ({parseResult.total_count.toLocaleString()})</button>
            <span className="text-gray-300">|</span>
            <button type="button" onClick={deselectAllManual} className="text-xs text-gray-500 hover:underline">Clear</button>
            {/* Top N by amount — judgmental only: auditor targets high-value items */}
            {amountCol && selectionMode === 'judgmental' && (
              <>
                <span className="text-gray-300">|</span>
                <button type="button" onClick={() => selectTopByAmount(5)}  className="text-xs text-purple-600 hover:underline whitespace-nowrap">Top 5 by amount</button>
                <button type="button" onClick={() => selectTopByAmount(10)} className="text-xs text-purple-600 hover:underline whitespace-nowrap">Top 10</button>
                <button type="button" onClick={() => selectTopByAmount(25)} className="text-xs text-purple-600 hover:underline whitespace-nowrap">Top 25</button>
              </>
            )}
          </>
        )}
        <span className="ml-auto text-xs text-gray-400 whitespace-nowrap">
          {filtered.length.toLocaleString()} row{filtered.length !== 1 ? 's' : ''} · {manualChecked.size} selected
        </span>
      </div>

      {/* No-approver notice */}
      {!hasApprover && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
          No approval column found in this file — obtain approval evidence per sample separately.
        </p>
      )}
      {parseResult.row_type_filter && (
        <p className="text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded-lg px-3 py-1.5">
          {parseResult.row_type_filter}
        </p>
      )}

      {/* Table */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {!readOnly && <th className="w-8 px-3 py-2"><input type="checkbox" className="h-3.5 w-3.5 rounded" onChange={e => e.target.checked ? selectAllManual() : deselectAllManual()} /></th>}
                {displayCols.map(col => (
                  <th key={col.key}
                    onClick={() => toggleSort(col.key)}
                    className="px-3 py-2 text-left font-medium text-gray-500 whitespace-nowrap cursor-pointer hover:text-blue-600 select-none"
                  >
                    {col.label}
                    {sortCol === col.key
                      ? <span className="ml-1 text-blue-500">{sortAsc ? '↑' : '↓'}</span>
                      : <ArrowUpDown className="inline ml-1 h-3 w-3 text-gray-300" />
                    }
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {pageRows.map(row => {
                const checked = manualChecked.has(row.ref);
                return (
                  <tr
                    key={row.ref}
                    onClick={() => !readOnly && toggleManualItem(row.ref)}
                    className={`${!readOnly ? 'cursor-pointer' : ''} ${checked ? 'bg-green-50' : 'hover:bg-gray-50'}`}
                  >
                    {!readOnly && (
                      <td className="px-3 py-1.5" onClick={e => e.stopPropagation()}>
                        <input type="checkbox" className="h-3.5 w-3.5 rounded text-blue-600" checked={checked} onChange={() => toggleManualItem(row.ref)} />
                      </td>
                    )}
                    {displayCols.map(col => (
                      <td key={col.key} className="px-3 py-1.5 max-w-[200px]">
                        {col.key === 'ref'
                          ? <span className={`font-medium ${checked ? 'text-green-800' : 'text-gray-800'}`}>{row.ref}</span>
                          : formatCell(col.key, row.fields[col.key])
                        }
                      </td>
                    ))}
                  </tr>
                );
              })}
              {pageRows.length === 0 && (
                <tr><td colSpan={displayCols.length + 1} className="px-3 py-6 text-center text-gray-400">No rows match your search.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-t border-gray-200">
            <button type="button" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
              className="text-xs px-2 py-1 border border-gray-300 rounded disabled:opacity-40 hover:bg-white">← Prev</button>
            <span className="text-xs text-gray-500">Page {page + 1} of {totalPages} ({filtered.length.toLocaleString()} rows)</span>
            <button type="button" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
              className="text-xs px-2 py-1 border border-gray-300 rounded disabled:opacity-40 hover:bg-white">Next →</button>
          </div>
        )}
      </div>

      {/* Confirm footer */}
      {!readOnly && confirmManual && (
        <div className="flex items-center justify-between pt-1">
          <span className="text-xs text-gray-500">{manualChecked.size} of {parseResult.total_count.toLocaleString()} items selected</span>
          <button type="button" onClick={confirmManual} disabled={manualChecked.size === 0}
            className="px-4 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 disabled:opacity-40">
            Confirm {manualChecked.size} samples →
          </button>
        </div>
      )}
    </div>
  );
}

function PopulationFilePanel({ upload, control, suppressBanner = false }) {
  const {
    phase, setPhase, parseResult, samplingPct, setSamplingPct, selectedSamples,
    selectionMode, setSelectionMode, manualChecked, setManualChecked, toggleManualItem, confirmManual,
    selectAllManual, deselectAllManual, removeSample,
    error, fileRef, handleFileUpload, handleSelectSamples, handleSelectRecommended,
    resetToUpload, handleColumnChange, reParsingColumn,
    systematicSize, setSystematicSize, handleSelectSystematic,
    reviewConfirmed, setReviewConfirmed,
    storeError, canUseLibrary, libraryOpen, setLibraryOpen, pickFromLibrary, storeCtx,
  } = upload;
  const needsReview = !!parseResult?.low_confidence && !reviewConfirmed;

  return (
    <div className="space-y-4 border border-gray-200 rounded-lg p-4 bg-gray-50/50">
      {!suppressBanner && (
        <>
          <div>
            <p className="text-sm font-medium text-gray-800">Population file</p>
            <p className="text-xs text-gray-500 mt-0.5">Upload the full population list, then pick samples randomly or hand-pick them manually.</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
            {populationPrompt(control)}
          </div>
        </>
      )}

      {phase === 'upload' && (
        <div>
          <input ref={fileRef} type="file" accept={POPULATION_ACCEPT} className="hidden" onChange={handleFileUpload} />
          <button type="button" onClick={() => fileRef.current?.click()}
            className="w-full border-2 border-dashed border-gray-300 rounded-xl p-6 flex flex-col items-center gap-2 hover:border-blue-400 transition-colors bg-white">
            <Upload className="h-7 w-7 text-gray-400" />
            <span className="text-sm text-gray-600">Click to upload population file</span>
            <span className="text-xs text-gray-400">{POPULATION_FORMATS_LABEL}</span>
          </button>
          {canUseLibrary && (
            <button type="button" onClick={() => setLibraryOpen(true)}
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 hover:underline">
              <Library className="h-3.5 w-3.5" /> or select a file already in the evidence library
            </button>
          )}
          {error && <p className="text-sm text-red-500 mt-2">{error}</p>}
        </div>
      )}
      {storeError && <p className="text-xs text-amber-700">{storeError}</p>}
      {libraryOpen && (
        <LibraryPicker
          title="Select population file from library"
          soxControlId={storeCtx.controlUuid}
          testRunId={storeCtx.runId || undefined}
          roles={['population']}
          single
          confirmLabel="Use this file"
          onConfirm={pickFromLibrary}
          onClose={() => setLibraryOpen(false)}
        />
      )}

      {(phase === 'parsing' || reParsingColumn) && (
        <div className="flex items-center gap-3 py-4 justify-center text-sm text-gray-600">
          <div className="h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          {reParsingColumn ? 'Applying column selection…' : 'Parsing population file…'}
        </div>
      )}

      {phase === 'sampling' && !parseResult && (
        <div className="space-y-3">
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
            Population file data is no longer available. Re-upload the file to change the sample selection.
          </p>
          <input ref={fileRef} type="file" accept={POPULATION_ACCEPT} className="hidden" onChange={handleFileUpload} />
          <button type="button" onClick={() => fileRef.current?.click()}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
            Re-upload population file
          </button>
        </div>
      )}

      {phase === 'sampling' && parseResult && !reParsingColumn && (
        <div className="space-y-3">
          {/* Column picker */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-blue-800 uppercase tracking-wide mb-0.5">Identifier column</p>
                <p className="text-sm text-blue-900 font-medium">{parseResult.identifier_column}</p>
                <p className="text-xs text-blue-700 mt-0.5">{parseResult.total_count} items found</p>
              </div>
              {parseResult.available_columns?.length > 1 && (
                <div className="shrink-0">
                  <label className="text-xs text-blue-700 font-medium block mb-1">Use a different column:</label>
                  <div className="relative">
                    <select
                      defaultValue={parseResult.identifier_column}
                      onChange={e => handleColumnChange(e.target.value)}
                      className="appearance-none text-xs bg-white border border-blue-300 rounded px-2 py-1.5 pr-6 text-gray-700 focus:outline-none cursor-pointer"
                    >
                      {parseResult.available_columns.map(col => (
                        <option key={col} value={col}>{col}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-blue-500 pointer-events-none" />
                  </div>
                </div>
              )}
            </div>
            {!parseResult.low_confidence && <ParseMessages result={parseResult} />}
          </div>

          {parseResult.low_confidence && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 space-y-2" data-testid="population-review-gate">
              <p className="text-xs font-semibold text-amber-900 flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5" /> Review the extracted rows
              </p>
              <ParseMessages result={parseResult} tone="amber" />
              <label className="flex items-start gap-2 text-xs text-amber-900 cursor-pointer">
                <input type="checkbox" checked={reviewConfirmed} onChange={e => setReviewConfirmed(e.target.checked)}
                  className="mt-0.5 h-3.5 w-3.5 rounded" />
                <span>I checked the {parseResult.total_count} extracted items against the source file. Continue to sampling.</span>
              </label>
            </div>
          )}

          {!needsReview && (<>
          {/* Sampling method toggle */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-500 font-medium">Sampling method:</span>
            {[
              { value: 'random',      label: 'Random' },
              { value: 'systematic',  label: 'Systematic' },
              { value: 'haphazard',   label: 'Haphazard' },
              { value: 'judgmental',  label: 'Judgmental' },
            ].map(m => (
              <button key={m.value} type="button" onClick={() => setSelectionMode(m.value)}
                className={`px-3 py-1 text-xs rounded-full border transition-colors ${selectionMode === m.value ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'}`}>
                {m.label}
              </button>
            ))}
          </div>

          {/* Method description chips */}
          {selectionMode === 'systematic' && (
            <p className="text-xs text-gray-500 bg-blue-50 border border-blue-100 rounded px-3 py-1.5">
              Every Nth item — start at a random point within the first interval.
            </p>
          )}
          {selectionMode === 'haphazard' && (
            <p className="text-xs text-gray-500 bg-blue-50 border border-blue-100 rounded px-3 py-1.5">
              Non-systematic manual selection without conscious bias. Document your selection rationale in the workpaper.
            </p>
          )}
          {selectionMode === 'judgmental' && (
            <p className="text-xs text-gray-500 bg-blue-50 border border-blue-100 rounded px-3 py-1.5">
              Selection based on professional judgment — focus on high-risk, high-value, or prior-exception items.
            </p>
          )}

          {selectionMode === 'random' && (
            <div className="space-y-2">
              {(() => {
                const rec = bandSampleSize(parseResult.total_count);
                return (
                  <div className="flex flex-wrap items-center gap-3 bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm text-green-800 flex-1 min-w-[12rem]">
                      Recommended: <strong>{rec.size}</strong> — {rec.rationale}
                    </p>
                    <button type="button" onClick={handleSelectRecommended}
                      className="px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700">
                      Pick {rec.size} random items
                    </button>
                  </div>
                );
              })()}
              <div className="flex flex-wrap items-center gap-2 text-sm bg-white border border-gray-200 rounded-lg p-3">
                <span className="text-gray-600">Custom:</span>
                <input type="number" min={1} max={100} value={samplingPct} onChange={e => setSamplingPct(Number(e.target.value))}
                  className="w-16 px-2 py-1 border border-gray-300 rounded text-sm" />
                <span className="text-gray-600">% of population</span>
                <span className="text-gray-800 font-medium">≈ {Math.max(1, Math.round(parseResult.total_count * samplingPct / 100))} items</span>
                <button type="button" onClick={handleSelectSamples}
                  className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 ml-auto">
                  Pick {Math.max(1, Math.round(parseResult.total_count * samplingPct / 100))} randomly
                </button>
              </div>
            </div>
          )}

          {selectionMode === 'systematic' && (() => {
            const rec = bandSampleSize(parseResult.total_count);
            const target = systematicSize ?? rec.size;
            const interval = Math.max(1, Math.floor(parseResult.total_count / target));
            return (
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-3 bg-white border border-gray-200 rounded-lg p-3">
                  <div>
                    <label className="text-xs text-gray-600 font-medium block mb-1">Sample size</label>
                    <input type="number" min={1} max={parseResult.total_count}
                      value={target}
                      onChange={e => setSystematicSize(Math.max(1, Number(e.target.value)))}
                      className="w-24 px-2 py-1 border border-gray-300 rounded text-sm" />
                  </div>
                  <div className="text-xs text-gray-500 flex-1">
                    <span className="font-medium text-gray-700">Interval: every {interval} item{interval !== 1 ? 's' : ''}</span>
                    <br />Start at a random item in the first {interval}-item block.
                  </div>
                  <button type="button" onClick={() => handleSelectSystematic(target)}
                    className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700">
                    Pick {target} systematically
                  </button>
                </div>
                <p className="text-xs text-gray-400">Recommended size: {rec.size} — {rec.rationale}</p>
              </div>
            );
          })()}

          {(selectionMode === 'haphazard' || selectionMode === 'judgmental') && (
            <PopulationTable
              parseResult={parseResult}
              manualChecked={manualChecked}
              setManualChecked={setManualChecked}
              toggleManualItem={toggleManualItem}
              selectAllManual={selectAllManual}
              deselectAllManual={deselectAllManual}
              confirmManual={confirmManual}
              selectionMode={selectionMode}
            />
          )}
          </>)}
        </div>
      )}

      {/* All samples removed — show recovery so user isn't stuck */}
      {phase === 'sampled' && selectedSamples.length === 0 && (
        <div className="border border-amber-200 bg-amber-50 rounded-lg px-4 py-3 flex items-center justify-between gap-3">
          <p className="text-sm text-amber-800">All samples removed. Go back to re-select.</p>
          <button
            type="button"
            onClick={() => parseResult?.items?.length > 0 ? setPhase('sampling') : resetToUpload()}
            className="text-xs font-semibold text-blue-600 hover:underline shrink-0"
          >
            {parseResult?.items?.length > 0 ? '← Re-select samples' : '← Re-upload file'}
          </button>
        </div>
      )}

      {phase === 'sampled' && selectedSamples.length > 0 && (
        <div className="space-y-3">
          <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 flex items-center justify-between">
            <p className="text-sm font-semibold text-green-900">
              {selectedSamples.length} sample{selectedSamples.length !== 1 ? 's' : ''} selected
              {parseResult?.total_count ? (
                <span className="font-normal text-green-800"> from {parseResult.total_count.toLocaleString()} items</span>
              ) : null}
            </p>
            <p className="text-xs text-green-700">Click × to remove individual items</p>
          </div>
          <div className="max-h-48 overflow-y-auto border border-green-200 rounded-lg divide-y bg-white">
            {selectedSamples.map((s, i) => (
              <div key={i} className="flex items-center gap-2 px-3 py-1.5">
                <span className="text-[10px] font-bold text-teal-700 bg-teal-50 border border-teal-200 rounded px-1.5 py-0.5 shrink-0">SAMPLE-{i + 1}</span>
                <span className="text-sm text-gray-800 flex-1 min-w-0 truncate">{s}</span>
                <button
                  type="button"
                  onClick={() => removeSample(s)}
                  className="shrink-0 p-0.5 text-gray-400 hover:text-red-500 rounded"
                  title="Remove this sample"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-4 pt-1">
            {parseResult?.items?.length > 0 && (
              <button type="button" onClick={() => setPhase('sampling')} className="text-xs text-blue-600 hover:underline">
                Change sample selection
              </button>
            )}
            <button type="button" onClick={resetToUpload} className="text-xs text-gray-500 hover:underline">
              Upload different file
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---- Non-event: population file OR period-based sampling (never both at once) ---- */
/**
 * IPETieoutPanel — replace the checkbox with a proper numeric tie-out.
 * Used whenever a population file is in play (event-based or file-upload path).
 *
 * Props:
 *   totalCount   — population total from the file (pre-fills "system total")
 *   value        — { filtered, independent, note }
 *   onChange     — called with updated value
 */
function IPETieoutPanel({ totalCount, value, onChange, currency = null }) {
  const filtered     = value?.filtered ?? (totalCount ? String(totalCount) : '');
  const independent  = value?.independent ?? '';
  const note         = value?.note ?? '';

  const fNum = parseFloat(filtered);
  const iNum = parseFloat(independent);
  const bothFilled = !isNaN(fNum) && !isNaN(iNum) && independent !== '';
  const delta = bothFilled ? Math.abs(fNum - iNum) : null;
  const tolerance = bothFilled ? Math.max(1, iNum * 0.001) : null;
  const tiedOut = bothFilled && delta <= tolerance;

  const set = (patch) => onChange({ filtered, independent, note, ...patch });

  return (
    <div className="space-y-3 border border-amber-200 rounded-lg p-4 bg-amber-50/50">
      <div>
        <p className="text-sm font-semibold text-amber-900">IPE Tie-out (required)</p>
        <p className="text-xs text-amber-700 mt-0.5">
          Enter the population total from the system and your independently verified total.
          They must agree within 0.1% (min 1) before sampling is allowed.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">System / filtered total{currencySuffix(currency)}</label>
          <input
            type="number" step="0.01" placeholder="e.g. 1250000.00"
            value={filtered}
            onChange={e => set({ filtered: e.target.value })}
            className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Independent total{currencySuffix(currency)}</label>
          <input
            type="number" step="0.01" placeholder="e.g. 1250000.00"
            value={independent}
            onChange={e => set({ independent: e.target.value })}
            className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>
      {bothFilled && (
        <div className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium ${tiedOut ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
          {tiedOut
            ? <>&#10003; Tied out — delta ${delta.toFixed(2)} within tolerance (${tolerance.toFixed(2)})</>
            : <>&#10007; Discrepancy — delta ${delta.toFixed(2)} exceeds tolerance (${tolerance.toFixed(2)})</>}
        </div>
      )}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Comparison note (optional)</label>
        <input
          type="text" placeholder="e.g. Agreed to subledger report dated 2026-06-30"
          value={note}
          onChange={e => set({ note: e.target.value })}
          className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500"
        />
      </div>
    </div>
  );
}

function NonEventPopulation({ periods, control, storeCtx, testType, initialPopulation, initialSamplingMethod, onComplete, onBack }) {
  const totalPop = periods.length;
  const rec = recommendedSampleSize(control?.frequency, testType);
  const upload = usePopulationFileUpload(initialPopulation, storeCtx);

  const [ipe, setIpe]                 = useState(!!initialPopulation?.ipe_validated);
  const [ipeTieout, setIpeTieout]     = useState({});
  const [samplingMethod, setMethod]   = useState(
    initialPopulation?.sampling_method
    || (initialSamplingMethod === 'judgmental' ? 'judgmental' : 'random')
  );
  const [sampleSize, setSampleSize]   = useState(
    initialPopulation?.sample_size || Math.min(totalPop || rec.min, rec.max) || rec.min
  );
  const [justification, setJustification] = useState('');

  const hasFileSamples = upload.selectedSamples.length > 0;
  const usingFilePopulation = hasFileSamples || upload.phase === 'sampling' || upload.phase === 'parsing';

  const outOfRange = !usingFilePopulation && (sampleSize < rec.min || sampleSize > rec.max);
  const needsJustification = outOfRange && !justification.trim();

  // IPE tie-out readiness for file-based path
  const ipeFNum = parseFloat(ipeTieout.filtered);
  const ipeINum = parseFloat(ipeTieout.independent);
  const ipeBothFilled = hasFileSamples && !isNaN(ipeFNum) && !isNaN(ipeINum) && ipeTieout.independent !== '';
  const ipeDelta = ipeBothFilled ? Math.abs(ipeFNum - ipeINum) : null;
  const ipeTolerance = ipeBothFilled ? Math.max(1, ipeINum * 0.001) : null;
  const ipeTiedOut = ipeBothFilled && ipeDelta <= ipeTolerance;

  const pickPeriodSamples = () => {
    const shuffled = [...periods].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, Math.min(sampleSize, periods.length)).map(p => p.label);
  };

  // Confirm waits for the population file's library save; a second click meanwhile must not save the run twice.
  const [confirming, setConfirming] = useState(false);
  const handleConfirm = async () => {
    if (confirming) return;
    setConfirming(true);
    try { await confirmPopulation(); } finally { setConfirming(false); }
  };
  const confirmPopulation = async () => {
    if (hasFileSamples) {
      // The file's library save may still be running: record the id of the file actually sampled.
      const populationFileId = await upload.waitForStoredUpload();
      onComplete({
        populationData: {
          file_name: upload.parseResult?.identifier_column,
          population_file_id: populationFileId ?? undefined,
          identifier_column: upload.parseResult?.identifier_column,
          total_count: upload.parseResult?.total_count,
          sampling_pct: upload.samplingPct,
          sample_size: upload.selectedSamples.length,
          selected_samples: upload.selectedSamples,
          sample_labels: Object.fromEntries(upload.selectedSamples.map((s, i) => [s, `SAMPLE-${i + 1}`])),
          items: upload.parseResult?.items,
          item_rows: selectedRowData(upload.parseResult, upload.selectedSamples),
          source: 'file_upload',
          filtered_total_amount: ipeBothFilled ? ipeFNum : undefined,
          independent_total_amount: ipeBothFilled ? ipeINum : undefined,
          ipe_comparison_note: ipeTieout.note || '',
        },
        totalPopulation: upload.parseResult?.total_count,
        sampleSize: upload.selectedSamples.length,
        ipeValidated: ipeTiedOut,
        samplingMethod: upload.selectionMode,
      });
      return;
    }
    const selected = pickPeriodSamples();
    onComplete({
      populationData: {
        total_count: totalPop,
        sample_size: selected.length,
        selected_samples: selected,
        sample_labels: Object.fromEntries(selected.map((s, i) => [s, `SAMPLE-${i + 1}`])),
        sampling_method: samplingMethod,
        source: 'periods',
      },
      totalPopulation: totalPop,
      sampleSize: selected.length,
      ipeValidated: ipe,
      samplingMethod,
      sampleSizeJustification: outOfRange ? justification : '',
    });
  };

  const canSave = hasFileSamples
    ? ipeTiedOut
    : ipe && !needsJustification && periods.length > 0;

  return (
    <div className="p-5 space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-1">Population & samples</h3>
        <p className="text-xs text-gray-500">
          {hasFileSamples
            ? 'Your samples come from the uploaded population file. Complete IPE tie-out to save.'
            : 'Upload a population file to pick specific items, or sample from your selected testing periods.'}
        </p>
      </div>

      {/* Option A — population file */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Option A — Population file</p>
        <PopulationFilePanel upload={upload} control={control} />
      </div>

      {/* IPE tie-out — shown when file samples are selected */}
      {hasFileSamples && (
        <IPETieoutPanel
          totalCount={upload.parseResult?.total_count}
          value={ipeTieout}
          onChange={setIpeTieout}
          currency={upload.parseResult?.currency}
        />
      )}

      {/* Option B — only when not using file samples */}
      {!hasFileSamples && (
        <>
          <div className="relative flex items-center py-1">
            <div className="flex-grow border-t border-gray-200" />
            <span className="flex-shrink mx-3 text-xs text-gray-400 uppercase">or sample from periods</span>
            <div className="flex-grow border-t border-gray-200" />
          </div>

          <div className="space-y-4 border border-gray-200 rounded-lg p-4 bg-gray-50/50">
            <p className="text-sm text-gray-700">
              No population file? Choose how many periods to test from your {periods.length} in-scope period{periods.length !== 1 ? 's' : ''}.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of periods to test</label>
                <input type="number" min={1} max={periods.length || 1} value={sampleSize}
                  onChange={e => setSampleSize(Number(e.target.value))}
                  className="w-28 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 bg-white" />
                <p className="text-xs text-gray-500 mt-1">Recommended: {rec.min}–{rec.max} ({rec.rationale})</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sampling method</label>
                <select value={samplingMethod} onChange={e => setMethod(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500">
                  <option value="random">Random</option>
                  <option value="haphazard">Haphazard</option>
                  <option value="judgmental">Judgmental</option>
                </select>
              </div>
            </div>
            {outOfRange && (
              <div>
                <label className="block text-sm font-medium text-red-600 mb-1">
                  Outside recommended range — justification required
                </label>
                <textarea value={justification} onChange={e => setJustification(e.target.value)} rows={2}
                  className="w-full px-3 py-2 border border-red-300 rounded-lg text-sm focus:ring-2 focus:ring-red-400 resize-y bg-white" />
              </div>
            )}
            <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Period</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Start</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">End</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {periods.map(p => (
                    <tr key={p.label}>
                      <td className="px-4 py-2 font-medium text-gray-800">{p.label}</td>
                      <td className="px-4 py-2 text-gray-600">{p.start}</td>
                      <td className="px-4 py-2 text-gray-600">{p.end}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Period-based: keep simple IPE checkbox */}
          <label className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer">
            <input type="checkbox" checked={ipe} onChange={e => setIpe(e.target.checked)} className="mt-0.5 h-4 w-4 text-blue-600 rounded" />
            <span className="text-sm text-amber-800">
              I confirm the population is <strong>complete and accurate</strong> (IPE validated) before testing.
            </span>
          </label>
        </>
      )}

      <div className="flex items-center gap-3">
        <button type="button" onClick={onBack} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">← Back</button>
        <button type="button" onClick={handleConfirm} disabled={!canSave || confirming}
          className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-40">
          {hasFileSamples
            ? `Save ${upload.selectedSamples.length} sample${upload.selectedSamples.length !== 1 ? 's' : ''} →`
            : 'Save configuration →'}
        </button>
      </div>
      {hasFileSamples && !ipeTiedOut && (
        <p className="text-xs text-amber-600 -mt-2">Complete IPE tie-out (both totals must agree) to save.</p>
      )}
      {!hasFileSamples && !ipe && (
        <p className="text-xs text-gray-400 -mt-2">Confirm IPE validation to continue.</p>
      )}
      {!hasFileSamples && upload.phase === 'sampling' && (
        <p className="text-xs text-amber-600 -mt-2">Finish selecting samples from your uploaded file, or use period sampling below.</p>
      )}
    </div>
  );
}

// ── useIPEFileUpload — upload a file to evidence store AND parse for count ────
// category: 'population' | 'ipe_proof' — stored on control_evidence.file_category
// Deduplicates by filename+category: skips the upload if the same file already exists.
// Returns { phase, result: { file_id, file_name, total_count }, parseData (full parse), errorMsg, fileRef, handleFile, reset }
function useIPEFileUpload(controlUuid, category = 'evidence', initialResult = null, ctx = {}) {
  const [phase,     setPhase]     = useState(initialResult ? 'done' : 'upload'); // upload | uploading | done | error
  const [result,    setResult]    = useState(initialResult);
  const [parseData, setParseData] = useState(null); // full parse result (items, identifier_column, etc.)
  const [errorMsg,  setErrorMsg]  = useState('');
  const [libraryOpen, setLibraryOpen] = useState(false);
  const fileRef = useRef(null);

  // Use a file already in the evidence library: parse its stored copy and link it to this control in this role.
  const pickFromLibrary = async (picks) => {
    const libFile = picks[0]?.file;
    if (!libFile) return;
    setPhase('uploading');
    setErrorMsg('');
    try {
      const fullParse = await parseLibraryPopulation(libFile.id);
      setParseData(fullParse);
      const linkResults = await linkLibraryFiles({
        sox_control_id: controlUuid,
        test_run_id: ctx.runId || undefined,
        items: [{
          control_upload_id: libFile.id,
          file_category: category,
          testing_period_start: dayOf(ctx.periodStart),
          testing_period_end: dayOf(ctx.periodEnd),
        }],
      });
      const linkFailed = (linkResults || []).find((r) => r.status === 'error');
      if (linkFailed) throw new Error(linkFailed.error || 'Could not link the file to this control');
      setResult({
        file_id: libFile.id, file_name: libFile.filename, total_count: fullParse.total_count,
        low_confidence: !!fullParse.low_confidence, notes: fullParse.low_confidence ? fullParse.notes : null,
      });
      setLibraryOpen(false);
      setPhase('done');
    } catch (err) {
      setErrorMsg(resolve403ErrorMessage(err, err.response?.data?.error || err.message || 'Failed to process file'));
      setLibraryOpen(false);
      setPhase('error');
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isSupportedPopulationFile(file)) {
      setErrorMsg(UNSUPPORTED_POPULATION_FILE_MESSAGE);
      setPhase('error');
      e.target.value = '';
      return;
    }
    setPhase('uploading');
    setErrorMsg('');
    try {
      // 1. Parse — keep the full response for sample selection
      const parseForm = new FormData();
      parseForm.append('file', file);
      const parseResp = await axiosInstance.post(
        '/api/test-runs/population/parse',
        parseForm,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const fullParse = parseResp.data;
      const total_count = fullParse.total_count;
      setParseData(fullParse);

      // 2. Upload to evidence store.
      // Reuse an existing file only when BOTH filename AND file size match —
      // same name but different size means different content (e.g. a newer filtered
      // extract) and must get its own record so Re-verify IPE uses the right file.
      let file_id = null;
      if (controlUuid) {
        try {
          const existing = await axiosInstance.get(`/api/sox-controls/${controlUuid}/evidence`);
          const found = (Array.isArray(existing.data) ? existing.data : [])
            .find(e =>
              e.filename === file.name &&
              (e.file_category || 'evidence') === category &&
              e.file_size === file.size   // same size ≈ same content
            );

          if (found) {
            file_id = found.control_upload_id ?? found.id ?? null;
          } else {
            const uploadForm = new FormData();
            uploadForm.append('file', file);
            uploadForm.append('file_category', category);
            if (ctx.runId) uploadForm.append('test_run_id', ctx.runId);
            const uploadResp = await axiosInstance.post(
              `/api/sox-controls/${controlUuid}/evidence/upload`,
              uploadForm,
              { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            file_id = uploadResp.data?.control_upload_id ?? uploadResp.data?.id ?? uploadResp.data?.file_id ?? null;
          }
        } catch {
          // Non-fatal — we still have the parsed data
        }
      }

      setResult({
        file_id, file_name: file.name, total_count,
        low_confidence: !!fullParse.low_confidence, notes: fullParse.low_confidence ? fullParse.notes : null,
      });
      setPhase('done');
    } catch (err) {
      setErrorMsg(resolve403ErrorMessage(err, err.response?.data?.error || 'Failed to process file'));
      setPhase('error');
    }
  };

  const reset = () => { setPhase('upload'); setResult(null); setParseData(null); setErrorMsg(''); if (fileRef.current) fileRef.current.value = ''; };
  return { phase, result, parseData, errorMsg, fileRef, handleFile, reset, libraryOpen, setLibraryOpen, pickFromLibrary, category, controlUuid, runId: ctx.runId };
}

// ── AttributePopulation — full IPE tie-out flow for attribute test runs ───────
function AttributePopulation({ control, controlUuid, runId, testType, testingPeriod = {}, initialPopulation, initialSamplingMethod, onComplete, onBack }) {
  // Restore file-upload hook state from saved population_data so Section A/B/C
  // show the previously uploaded files and verification result on re-open.
  // population_file_name was added to the save payload later.
  // For runs saved before that fix, we still have population_file_id — synthesise
  // a display label so Section A enters 'done' state and unlocks B/C/D.
  const popFileId = initialPopulation?.population_file_id ?? null;
  const popFileName = initialPopulation?.population_file_name
    || (popFileId ? `Population file (ID:${popFileId})` : null)
    || null;
  // What was read from the files last time, so a reopened (or rerun) tie-out compares the same figures.
  const savedMeta = initialPopulation?.parse_metadata || {};
  const savedPop = popFileName
    ? { file_id: popFileId,
        file_name: popFileName,
        total_count: initialPopulation.ipe_population_record_count ?? initialPopulation.total_count ?? null,
        total_amount: initialPopulation.filtered_total_amount ?? null,
        column_count: initialPopulation.ipe_population_column_count ?? null,
        date_range: savedMeta.population_date_range ?? null,
        currency: savedMeta.currency ?? null }
    : null;
  const savedIpe = initialPopulation?.ipe_proof_file_name
    ? { file_id: initialPopulation.ipe_proof_file_id ?? null,
        file_name: initialPopulation.ipe_proof_file_name,
        // fall back to total_count when ipe_proof_record_count not stored (older runs)
        total_count: initialPopulation.ipe_proof_record_count
          ?? (initialPopulation.ipe_status === 'verified' ? (initialPopulation.ipe_population_record_count ?? initialPopulation.total_count) : null)
          ?? null,
        total_amount: initialPopulation.independent_total_amount ?? null,
        column_count: initialPopulation.ipe_proof_column_count ?? null,
        date_range: savedMeta.ipe_date_range ?? null,
        currency: savedMeta.currency ?? null }
    : null;

  const fileCtx = { runId, periodStart: testingPeriod.start, periodEnd: testingPeriod.end };
  const popUpload = useIPEFileUpload(controlUuid, 'population', savedPop, fileCtx);  // population file (client-provided)
  const ipeUpload = useIPEFileUpload(controlUuid, 'ipe_proof',  savedIpe, fileCtx);  // IPE proof file (auditor-obtained)

  // Tie-out state — F5 fix: restore from initialPopulation so the "configure"
  // wizard doesn't start fresh when the run already has a completed tie-out.
  // ipe_tie_out in the backend is a *float64 (not the full object), so we
  // reconstruct a compatible ipeResult from the richer population_data fields.
  const [sourceNote,  setSourceNote]  = useState(initialPopulation?.ipe_source_note || '');
  const [popValue,    setPopValue]    = useState('');   // total value from population file (manual)
  const [ipeValue,    setIpeValue]    = useState('');   // total value from IPE proof (manual)
  const [ipeResult,   setIpeResult]   = useState(() => {
    const pop = initialPopulation;
    if (!pop?.ipe_status || pop.ipe_status === 'pending') return null;
    // ipe_population_record_count / ipe_proof_record_count are set by the
    // background ipe-verify call; fall back to total_count for older runs
    // that were saved before those fields were stored.
    const popCount = pop.ipe_population_record_count ?? pop.total_count ?? null;
    // If verified and proof count not stored, infer it equals population count.
    // NOTE: the actual IPE count may have differed within tolerance (e.g. 1645 vs 1646).
    // ipe_status is the authoritative result; this is display-only for the restored table.
    // "tied_out" (manual tie-out) and "verified" (AI verify) are both a tie-out that passed.
    const passedStatus = pop.ipe_status === 'verified' || pop.ipe_status === 'tied_out';
    const ipeCount = pop.ipe_proof_record_count
      ?? (passedStatus ? popCount : null)
      ?? null;
    const countDelta = (popCount != null && ipeCount != null) ? Math.abs(popCount - ipeCount) : null;
    const countTolerance = (popCount != null && ipeCount != null)
      ? Math.max(1, Math.round(Math.max(popCount, ipeCount) * 0.001))
      : null;
    const pv = pop.filtered_total_amount ?? null;
    const iv = pop.independent_total_amount ?? null;
    const bothValues = pv != null && iv != null;
    return {
      passed:           passedStatus,
      documentedAt:     pop.ipe_verified_at || new Date().toISOString(),
      sourceNote:       pop.ipe_source_note || '',
      // Counts
      popCount,
      ipeCount,
      countDelta,
      countTolerance,
      countMatch:       passedStatus || (countDelta != null && countTolerance != null && countDelta <= countTolerance),
      // Amounts (stored; enrichment fields not stored so will be null = rows hidden)
      bothValues,
      popValue:         pv,
      ipeValue:         iv,
      valueDelta:       pop.ipe_delta ?? (bothValues ? Math.abs(pv - iv) : null),
      valueTolerance:   bothValues ? Math.max(1, Math.round(Math.max(pv, iv) * 0.001)) : null,
      // From the figures when both are stored, else the saved result.
      // The saved result decides, as for the counts: recomputing with another tolerance could disagree with it.
      valueMatch:       passedStatus || (bothValues ? Math.abs(pv - iv) <= Math.max(0.01, Math.max(Math.abs(pv), Math.abs(iv)) * 0.001) : null),
      popAmountColumn:  null,   // not stored — hidden in restored view
      amountSource:     bothValues ? 'stored' : null,
      // Date ranges and currency come back from the parse metadata; column counts and quality signals are not stored
      popDateRange:     pop.parse_metadata?.population_date_range ?? null,
      ipeDateRange:     pop.parse_metadata?.ipe_date_range ?? null,
      dateRangeMatch:   (pop.parse_metadata?.population_date_range && pop.parse_metadata?.ipe_date_range)
        ? pop.parse_metadata.population_date_range.from === pop.parse_metadata.ipe_date_range.from
          && pop.parse_metadata.population_date_range.to === pop.parse_metadata.ipe_date_range.to
        : null,
      currency:         pop.parse_metadata?.currency ?? null,
      popDateColumn:    null,
      popColCount:      null,
      ipeColCount:      null,
      colCountMatch:    null,
      popDuplicates:    null,
      popNullAmounts:   null,
      popTop5Pct:       null,
      popRowTypeFilter: null,
      // File references
      populationFileId:   pop.population_file_id ?? null,
      populationFileName: pop.population_file_name ?? null,
      ipeProofFileId:     pop.ipe_proof_file_id ?? null,
      ipeProofFileName:   pop.ipe_proof_file_name ?? null,
    };
  });

  // Clear verification result when the IPE proof file is removed/reset —
  // but skip on initial mount so a restored ipeResult isn't wiped immediately.
  const ipeUploadMountedRef = useRef(false);
  React.useEffect(() => {
    if (!ipeUploadMountedRef.current) {
      ipeUploadMountedRef.current = true;
      return;
    }
    if (ipeUpload.phase === 'upload') {
      setIpeResult(null);
    }
  }, [ipeUpload.phase]); // eslint-disable-line

  // Sample selection — seeded from Section A parse so user never re-uploads
  const popUploadForSampling = usePopulationFileUpload(initialPopulation, {
    controlUuid, runId, periodStart: testingPeriod.start, periodEnd: testingPeriod.end,
  });

  // When Section A finishes parsing, auto-feed the parse result into Section D
  // so the user doesn't need to upload the population file a second time.
  // A newer upload (re-upload, other file) replaces what Section D holds: stale rows and samples must not survive it.
  const lastFedParse = useRef(null);
  React.useEffect(() => {
    if (popUpload.phase === 'done' && popUpload.parseData && lastFedParse.current !== popUpload.parseData) {
      lastFedParse.current = popUpload.parseData;
      popUploadForSampling.resetToUpload();
      popUploadForSampling.setParseResult(popUpload.parseData);
      popUploadForSampling.setLibraryFileId(popUpload.result?.file_id);
      popUploadForSampling.setPhase('sampling');
    }
  }, [popUpload.phase, popUpload.parseData]); // eslint-disable-line

  const popParsed  = popUpload.phase === 'done' && popUpload.result;
  const ipeParsed  = ipeUpload.phase === 'done' && ipeUpload.result;
  const canCompare = popParsed && ipeParsed;

  const runTieout = () => {
    const popCount = popUpload.result.total_count;
    const ipeCount = ipeUpload.result.total_count;
    const popParse = popUpload.parseData || {};
    const ipeParse = ipeUpload.parseData || {};

    // ── Count comparison (0.1% tolerance) ─────────────────────────────────
    const countDelta     = Math.abs(popCount - ipeCount);
    const countTolerance = Math.max(1, Math.round(Math.max(popCount, ipeCount) * 0.001));
    const countMatch     = countDelta <= countTolerance;

    // ── Amount comparison ──────────────────────────────────────────────────
    // Prefer auto-detected totals from the parser; fall back to manual entry.
    const autoPopAmt = popParse.total_amount ?? null;
    const autoIpeAmt = ipeParse.total_amount ?? null;
    const manualPv   = popValue.trim() ? parseFloat(popValue.replace(/,/g, '')) : NaN;
    const manualIv   = ipeValue.trim() ? parseFloat(ipeValue.replace(/,/g, '')) : NaN;
    const pv = !isNaN(manualPv) ? manualPv : autoPopAmt;
    const iv = !isNaN(manualIv) ? manualIv : autoIpeAmt;
    const bothValues     = pv !== null && pv !== undefined && !isNaN(pv) &&
                           iv !== null && iv !== undefined && !isNaN(iv);
    const valueDelta     = bothValues ? Math.abs(pv - iv) : null;
    const valueTolerance = bothValues ? Math.max(0.01, Math.max(pv, iv) * 0.001) : null;
    const valueMatch     = bothValues ? valueDelta <= valueTolerance : null;
    const amountSource   = (!isNaN(manualPv) && !isNaN(manualIv)) ? 'manual' : 'auto-detected';

    // ── Date range comparison ──────────────────────────────────────────────
    const popDateRange = popParse.date_range ?? null;   // {from, to, column}
    const ipeDateRange = ipeParse.date_range ?? null;
    let dateRangeMatch = null;
    if (popDateRange && ipeDateRange) {
      dateRangeMatch = popDateRange.from === ipeDateRange.from && popDateRange.to === ipeDateRange.to;
    }

    // ── Column structure ───────────────────────────────────────────────────
    const popColCount = popParse.column_count ?? null;
    const ipeColCount = ipeParse.column_count ?? null;
    const colCountMatch = (popColCount !== null && ipeColCount !== null)
      ? popColCount === ipeColCount : null;

    // ── Overall pass/fail ──────────────────────────────────────────────────
    // Count MUST match. Value must match if present. Date range mismatch is a warning, not a blocker.
    const passed = countMatch && (valueMatch === null || valueMatch === true);

    setIpeResult({
      passed,
      // Count
      countMatch, countDelta, countTolerance, popCount, ipeCount,
      // Amount
      bothValues, valueDelta, valueTolerance, valueMatch,
      popValue: pv, ipeValue: iv, amountSource,
      popAmountColumn: popParse.amount_column ?? null,
      ipeAmountColumn: ipeParse.amount_column ?? null,
      // Date range
      popDateRange, ipeDateRange, dateRangeMatch,
      popDateColumn: popParse.date_column ?? null,
      // Currency the amounts are in (the one marked most in the files)
      currency: popParse.currency || ipeParse.currency || null,
      ipeDateColumn: ipeParse.date_column ?? null,
      // Structure
      popColCount, ipeColCount, colCountMatch,
      // Quality signals
      popDuplicates:       popParse.duplicate_count   ?? 0,
      ipeDuplicates:       ipeParse.duplicate_count   ?? 0,
      popNullAmounts:      popParse.null_amount_count ?? 0,
      ipeNullAmounts:      ipeParse.null_amount_count ?? 0,
      popTop5Pct:          popParse.top5_concentration_pct ?? null,
      // Row filter notes
      popRowTypeFilter:    popParse.row_type_filter ?? null,
      ipeRowTypeFilter:    ipeParse.row_type_filter ?? null,
      // File metadata
      documentedAt:       new Date().toISOString(),
      populationFileName: popUpload.result.file_name,
      ipeFileName:        ipeUpload.result.file_name,
      populationFileId:   popUpload.result.file_id,
      ipeProofFileId:     ipeUpload.result.file_id,
      sourceNote,
    });
  };

  const ipeVerified = ipeResult?.passed === true;
  const popReady    = popUploadForSampling.phase === 'sampled' && popUploadForSampling.selectedSamples.length > 0;
  const canSave     = popReady && ipeVerified;

  const handleSave = () => {
    const pop = popUploadForSampling.parseResult || {};
    const sm  = popUploadForSampling.selectionMode || initialSamplingMethod || 'statistical';
    onComplete({
      populationData: {
        file_name:              popUpload.result?.file_name,
        identifier_column:      pop.identifier_column,
        total_count:            popUpload.result?.total_count,
        sample_size:            popUploadForSampling.selectedSamples.length,
        selected_samples:       popUploadForSampling.selectedSamples,
        sample_labels:          Object.fromEntries(popUploadForSampling.selectedSamples.map((s, i) => [s, `SAMPLE-${i + 1}`])),
        items:                  pop.items,
        item_rows:              selectedRowData(pop, popUploadForSampling.selectedSamples),
        source:                 'file_upload',
        population_file_id:     ipeResult?.populationFileId,
        ipe_proof_file_id:      ipeResult?.ipeProofFileId,
        ipe_proof_file_name:    ipeUpload.result?.file_name,
        ipe_source_note:        sourceNote,
        ipe_comparison_note:    sourceNote,
        // IPE comparison metrics — persisted so the Population C/A tab can display them.
        filtered_total_amount:        ipeResult?.popValue      ?? null,
        independent_total_amount:     ipeResult?.ipeValue      ?? null,
        ipe_population_record_count:  ipeResult?.popCount      ?? null,
        ipe_proof_record_count:       ipeResult?.ipeCount      ?? null,
        ipe_population_column_count:  ipeResult?.popColCount   ?? null,
        ipe_proof_column_count:       ipeResult?.ipeColCount   ?? null,
        // Dates each file covers and their currency: no column of their own, so kept with the parse metadata.
        parse_metadata: (() => {
          const meta = {
            population_date_range: ipeResult?.popDateRange ?? popUpload.result?.date_range ?? null,
            ipe_date_range:        ipeResult?.ipeDateRange ?? null,
            currency:              ipeResult?.currency ?? popUpload.result?.currency ?? null,
          };
          // Nothing known: send none, so the saved metadata is kept rather than replaced with nulls.
          return Object.values(meta).some((v) => v != null) ? meta : undefined;
        })(),
      },
      totalPopulation:  popUpload.result?.total_count,
      sampleSize:       popUploadForSampling.selectedSamples.length,
      ipeValidated:     ipeVerified,
      samplingMethod:   sm,
    });
  };

  // Shared file upload box UI
  const FileUploadBox = ({ label, sublabel, icon: Icon, hook, accentClass, borderClass, validate }) => {
    const handleFileChange = (e) => {
      const file = e.target.files?.[0];
      if (file && validate) {
        const errMsg = validate(file.name);
        if (errMsg) {
          toast.error(errMsg);
          e.target.value = '';
          return;
        }
      }
      hook.handleFile(e);
    };
    return (
    <div className={`space-y-2 border ${borderClass} rounded-xl p-4`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-semibold text-gray-800">{label}</p>
          <p className="text-xs text-gray-500 mt-0.5">{sublabel}</p>
        </div>
        {hook.phase === 'done' && hook.result && (
          <button type="button" onClick={hook.reset} className="text-gray-300 hover:text-red-400">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {hook.phase === 'upload' && (
        <>
          <input ref={hook.fileRef} type="file" accept={POPULATION_ACCEPT} className="hidden" onChange={handleFileChange} />
          <button type="button" onClick={() => hook.fileRef.current?.click()}
            className={`w-full border-2 border-dashed ${accentClass} rounded-xl p-5 flex flex-col items-center gap-2 hover:opacity-80 transition-opacity`}>
            <Icon className="h-6 w-6" />
            <span className="text-sm font-medium">Click to upload</span>
            <span className="text-xs opacity-70">{POPULATION_FORMATS_LABEL}</span>
          </button>
          {hook.controlUuid && (
            <button type="button" onClick={() => hook.setLibraryOpen(true)}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 hover:underline">
              <Library className="h-3.5 w-3.5" /> or select from the evidence library
            </button>
          )}
        </>
      )}

      {hook.phase === 'uploading' && (
        <div className="flex items-center gap-3 py-4 justify-center text-sm text-gray-500">
          <div className="h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          Uploading & parsing…
        </div>
      )}

      {hook.phase === 'error' && (
        <div className="space-y-2">
          <p className="text-xs text-red-500">{hook.errorMsg}</p>
          <input ref={hook.fileRef} type="file" accept={POPULATION_ACCEPT} className="hidden" onChange={handleFileChange} />
          <button type="button" onClick={() => hook.fileRef.current?.click()}
            className="px-3 py-1.5 bg-red-100 text-red-700 text-xs rounded-lg hover:bg-red-200">
            Try again
          </button>
        </div>
      )}

      {hook.phase === 'done' && hook.result && (
        <div className="bg-gray-50 rounded-lg px-3 py-2.5 flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-800 truncate max-w-[220px]" title={hook.result.file_name}>
              {hook.result.file_name}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              {hook.result.total_count?.toLocaleString()} records
              {hook.result.file_id ? <span className="ml-2 text-green-600">· Saved</span> : <span className="ml-2 text-amber-500">· Not stored (no run yet)</span>}
            </p>
            {hook.result.low_confidence && (
              <p className="text-xs text-amber-700 mt-1 flex items-start gap-1">
                <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" /> Read with AI — check the rows below before relying on them.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
    );
  };

  return (
    <div className="p-5 space-y-5">
      <div>
        <h3 className="text-sm font-semibold text-gray-800">Population</h3>
      </div>

      {/* ── Section A + B: Dual file upload ───────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-0.5 rounded-md bg-blue-600 text-white text-[11px] font-bold shrink-0">A</span>
            <span className="text-sm font-semibold text-gray-800">Population List</span>
          </div>
          <FileUploadBox
            label="Client-provided population"
            sublabel="Detail list from the client's system (GL extract, AP aging, etc.)"
            icon={Upload}
            hook={popUpload}
            accentClass="border-blue-300 bg-blue-50/40 text-blue-700"
            borderClass="border-blue-200 bg-blue-50/20"
          />
        </div>
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className={`px-2 py-0.5 rounded-md text-white text-[11px] font-bold shrink-0 ${popParsed ? 'bg-amber-500' : 'bg-gray-300'}`}>B</span>
            <span className={`text-sm font-semibold ${popParsed ? 'text-gray-800' : 'text-gray-400'}`}>IPE Proof</span>
          </div>
          {!popParsed
            ? <div className="border border-dashed border-gray-200 rounded-xl p-6 text-center">
                <p className="text-xs text-gray-400">Upload population file (A) first</p>
              </div>
            : <FileUploadBox
                label="Auditor-obtained independent proof"
                sublabel="Upload evidence for population completeness and accuracy verification"
                icon={Shield}
                hook={ipeUpload}
                accentClass="border-amber-300 bg-amber-50/40 text-amber-700"
                borderClass="border-amber-200 bg-amber-50/20"
                validate={(name) => {
                  if (popUpload.result?.file_name && name === popUpload.result.file_name) {
                    return 'The verification file cannot be the same as the population file. Please upload a different file.';
                  }
                  return null;
                }}
              />
          }
        </div>
      </div>

      {/* ── Section C: Completeness & Accuracy Verification ──────────────── */}
      <div>
        {/* Pickers live here, not in FileUploadBox: that box is redefined on every render and would remount them. */}
        {[popUpload, ipeUpload].filter(h => h.libraryOpen).map(h => (
          <LibraryPicker
            key={h.category}
            title={h.category === 'ipe_proof' ? 'Select IPE proof from library' : 'Select population file from library'}
            soxControlId={controlUuid}
            testRunId={runId || undefined}
            roles={[h.category]}
            single
            confirmLabel="Use this file"
            onConfirm={h.pickFromLibrary}
            onClose={() => h.setLibraryOpen(false)}
          />
        ))}
        <InputChecksBanner controlUuid={controlUuid} populationFileId={popUpload.result?.file_id} ipeProofFileId={ipeUpload.result?.file_id} />
        <div className="flex items-center gap-2 mb-2">
          <span className={`px-2 py-0.5 rounded-md text-white text-[11px] font-bold shrink-0 ${canCompare ? 'bg-blue-600' : 'bg-gray-300'}`}>C</span>
          <span className={`text-sm font-semibold ${canCompare ? 'text-gray-800' : 'text-gray-400'}`}>Completeness &amp; Accuracy Verification</span>
          {ipeResult && (
            <span className={`ml-auto text-xs font-semibold px-2 py-0.5 rounded-full ${ipeResult.passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {ipeResult.passed ? 'Tied Out' : 'Exception'}
            </span>
          )}
        </div>

        {!canCompare && (
          <p className="text-xs text-gray-400 pl-7">Upload both files above to enable tie-out.</p>
        )}

        {canCompare && !ipeResult && (
          <div className="border border-gray-200 rounded-xl p-4 space-y-4">
            {/* Auto count comparison preview — compact horizontal */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 flex items-center justify-between">
                <span className="text-xs font-semibold text-blue-600">Population</span>
                <div>
                  <span className="text-base font-bold text-blue-800">{popUpload.result.total_count?.toLocaleString() ?? '—'}</span>
                  <span className="text-[10px] text-blue-400 ml-1">records</span>
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 flex items-center justify-between">
                <span className="text-xs font-semibold text-amber-600">IPE Proof</span>
                <div>
                  <span className="text-base font-bold text-amber-800">{ipeUpload.result.total_count?.toLocaleString() ?? '—'}</span>
                  <span className="text-[10px] text-amber-400 ml-1">records</span>
                </div>
              </div>
            </div>

            {/* Manual value entry */}
            <div>
              <p className="text-xs font-semibold text-gray-700 mb-2">
                Total value tie-out <span className="text-gray-400 font-normal">(optional — enter if your control is value-based)</span>
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] text-gray-500 font-medium mb-1">Population total{currencySuffix(popUpload.result?.currency)}</label>
                  <input type="text" value={popValue} onChange={e => setPopValue(e.target.value)}
                    placeholder="e.g. 1,250,000.00"
                    className="w-full px-3 py-1.5 border border-blue-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-400" />
                </div>
                <div>
                  <label className="block text-[10px] text-gray-500 font-medium mb-1">IPE proof total{currencySuffix(ipeUpload.result?.currency || popUpload.result?.currency)}</label>
                  <input type="text" value={ipeValue} onChange={e => setIpeValue(e.target.value)}
                    placeholder="e.g. 1,250,000.00"
                    className="w-full px-3 py-1.5 border border-amber-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-amber-400" />
                </div>
              </div>
              <p className="text-[10px] text-gray-400 mt-1">Leave blank to tie out on count only. Tolerance = 0.1% on both count and value.</p>
            </div>

            <button type="button" onClick={runTieout}
              className="w-full py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700">
              Run Completeness &amp; Accuracy Verification
            </button>
          </div>
        )}

        {ipeResult && (
          <div className={`border rounded-xl p-4 space-y-4 ${ipeResult.passed ? 'border-green-200 bg-green-50/40' : 'border-red-200 bg-red-50/40'}`}>

            {/* ── Header ─────────────────────────────────────────────────── */}
            <div className="flex items-start gap-3">
              {ipeResult.passed
                ? <CheckCircle className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
                : <XCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />}
              <div>
                <p className={`text-sm font-bold ${ipeResult.passed ? 'text-green-900' : 'text-red-900'}`}>
                  {ipeResult.passed ? 'IPE Tied Out — Population Verified' : 'IPE Exception — Discrepancy Exceeds Tolerance'}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Documented {new Date(ipeResult.documentedAt).toLocaleString()}
                </p>
              </div>
            </div>

            {/* ── Primary tie-out table ───────────────────────────────────── */}
            <div className="border border-gray-200 rounded-lg overflow-hidden bg-white text-xs">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide">Metric</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-500 uppercase tracking-wide">Population</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-500 uppercase tracking-wide">IPE Proof</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-500 uppercase tracking-wide">Diff</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-500 uppercase tracking-wide">Tolerance</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-500 uppercase tracking-wide">Result</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">

                  {/* Record count */}
                  <tr>
                    <td className="px-3 py-2 font-medium text-gray-700">
                      Record count
                      <span className="block text-[10px] font-normal text-gray-400">
                        0.1% tolerance = {ipeResult.countTolerance?.toLocaleString() ?? '—'} record{ipeResult.countTolerance !== 1 ? 's' : ''}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right text-gray-800 font-medium">{ipeResult.popCount?.toLocaleString() ?? '—'}</td>
                    <td className="px-3 py-2 text-right text-gray-800 font-medium">{ipeResult.ipeCount?.toLocaleString() ?? '—'}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{ipeResult.countDelta?.toLocaleString() ?? '—'}</td>
                    <td className="px-3 py-2 text-right text-gray-500">{ipeResult.countTolerance?.toLocaleString() ?? '—'}</td>
                    <td className="px-3 py-2 text-center">
                      {ipeResult.countMatch
                        ? <span className="text-green-600 font-semibold">✓ Match</span>
                        : <span className="text-red-600 font-semibold">✗ Exception</span>}
                    </td>
                  </tr>

                  {/* Total amount */}
                  {ipeResult.bothValues && (
                    <tr>
                      <td className="px-3 py-2 font-medium text-gray-700">
                        Total amount
                        {ipeResult.popAmountColumn && (
                          <span className="block text-[10px] font-normal text-gray-400">
                            Column: {ipeResult.popAmountColumn}
                            {ipeResult.amountSource === 'auto-detected' && ' · auto-detected'}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-800 font-medium">
                        {ipeResult.popValue != null ? formatMoney(ipeResult.popValue, ipeResult.currency) : null}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-800 font-medium">
                        {ipeResult.ipeValue != null ? formatMoney(ipeResult.ipeValue, ipeResult.currency) : null}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-600">
                        {ipeResult.valueDelta != null ? formatMoney(ipeResult.valueDelta, ipeResult.currency) : null}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-500">
                        {ipeResult.valueTolerance != null ? formatMoney(ipeResult.valueTolerance, ipeResult.currency) : null}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {ipeResult.valueMatch
                          ? <span className="text-green-600 font-semibold">✓ Match</span>
                          : <span className="text-red-600 font-semibold">✗ Exception</span>}
                      </td>
                    </tr>
                  )}

                  {/* Period covered: the dates each file spans, against the testing period */}
                  {(() => {
                    const pop = completeYear(ipeResult.popDateRange, testingPeriod.start, testingPeriod.end);
                    const ipe = completeYear(ipeResult.ipeDateRange, testingPeriod.start, testingPeriod.end);
                    const result = periodResult([pop, ipe], testingPeriod.start, testingPeriod.end);
                    const same = pop && ipe ? pop.from === ipe.from && pop.to === ipe.to : null;
                    return (
                      <tr data-testid="period-row">
                        <td className="px-3 py-2 font-medium text-gray-700">
                          Period covered
                          <span className="block text-[10px] font-normal text-gray-400">
                            {ipeResult.popDateColumn ? `Column: ${ipeResult.popDateColumn}` : 'Dates in the files'}
                            {(pop?.yearFromPeriod || ipe?.yearFromPeriod) ? ' · year not printed, taken from the testing period' : ''}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right text-gray-800">{formatRange(pop)}</td>
                        <td className="px-3 py-2 text-right text-gray-800">{formatRange(ipe)}</td>
                        <td className="px-3 py-2 text-right text-gray-600">{same === true ? 'Same' : same === false ? 'Differ' : '—'}</td>
                        <td className="px-3 py-2 text-right text-gray-500 text-[11px]">
                          {testingPeriod.start && testingPeriod.end ? <>Testing period<br />{testingPeriod.start} – {testingPeriod.end}</> : '—'}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {result === 'within'
                            ? <span className="text-green-600 font-semibold">✓ Within period</span>
                            : result === 'outside'
                              ? <span className="text-red-600 font-semibold">✗ Outside period</span>
                              : <span className="text-gray-400">{ipeResult.amountSource === 'stored' && !ipeResult.popDateRange && !ipeResult.ipeDateRange ? 'Not recorded for this run' : 'Dates not found'}</span>}
                        </td>
                      </tr>
                    );
                  })()}

                  {/* Column count */}
                  {ipeResult.popColCount !== null && ipeResult.ipeColCount !== null && (
                    <tr>
                      <td className="px-3 py-2 font-medium text-gray-700">
                        Column count
                        <span className="block text-[10px] font-normal text-gray-400">File structure check</span>
                      </td>
                      <td className="px-3 py-2 text-right text-gray-800">{ipeResult.popColCount}</td>
                      <td className="px-3 py-2 text-right text-gray-800">{ipeResult.ipeColCount}</td>
                      <td className="px-3 py-2 text-right text-gray-600">{Math.abs(ipeResult.popColCount - ipeResult.ipeColCount)}</td>
                      <td className="px-3 py-2 text-right text-gray-400">0</td>
                      <td className="px-3 py-2 text-center">
                        {ipeResult.colCountMatch
                          ? <span className="text-green-600 font-semibold">✓ Match</span>
                          : <span className="text-amber-600 font-semibold">⚠ Differs</span>}
                      </td>
                    </tr>
                  )}

                </tbody>
              </table>
            </div>

            {/* ── Quality signals ─────────────────────────────────────────── */}
            {(ipeResult.popDuplicates > 0 || ipeResult.popNullAmounts > 0 || ipeResult.popTop5Pct !== null || ipeResult.popRowTypeFilter) && (
              <div className="bg-amber-50 border border-amber-100 rounded-lg px-3 py-2.5 space-y-1">
                <p className="text-[10px] font-semibold text-amber-700 uppercase tracking-wide mb-1.5">Population Quality Notes</p>
                {ipeResult.popRowTypeFilter && (
                  <p className="text-xs text-amber-800">📋 {ipeResult.popRowTypeFilter}</p>
                )}
                {ipeResult.popDuplicates > 0 && (
                  <p className="text-xs text-amber-800">
                    ⚠ {ipeResult.popDuplicates.toLocaleString()} identifier{ipeResult.popDuplicates !== 1 ? 's are' : ' is'} used by more than one record — each is kept; check they are not duplicates
                  </p>
                )}
                {ipeResult.popNullAmounts > 0 && ipeResult.popAmountColumn && (
                  <p className="text-xs text-amber-800">
                    ⚠ {ipeResult.popNullAmounts.toLocaleString()} row{ipeResult.popNullAmounts !== 1 ? 's' : ''} had no value in "{ipeResult.popAmountColumn}" and were excluded from total
                  </p>
                )}
                {ipeResult.popTop5Pct !== null && (
                  <p className="text-xs text-gray-600">
                    📊 Top 5 items by amount represent <strong>{ipeResult.popTop5Pct}%</strong> of total population value
                  </p>
                )}
              </div>
            )}

            {/* ── File metadata ───────────────────────────────────────────── */}
            <div className="text-xs space-y-1 text-gray-600 border-t border-gray-100 pt-3">
              <p>
                <span className="font-medium">Population file:</span>{' '}
                {ipeResult.populationFileName}
              </p>
              <p>
                <span className="font-medium">IPE proof file:</span>{' '}
                {ipeResult.ipeFileName}
              </p>
              {ipeResult.sourceNote && (
                <p><span className="font-medium">Source system:</span> {ipeResult.sourceNote}</p>
              )}
            </div>

            {!ipeResult.passed && (
              <button type="button"
                onClick={() => { setIpeResult(null); ipeUpload.reset(); setSourceNote(''); }}
                className="text-xs text-blue-600 hover:underline">
                Re-upload IPE proof and re-run tie-out
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Section D: Sample selection ────────────────────────────────────── */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <span className={`px-2 py-0.5 rounded-md text-white text-[11px] font-bold shrink-0 ${ipeVerified ? 'bg-blue-600' : 'bg-gray-300'}`}>D</span>
          <span className={`text-sm font-semibold ${ipeVerified ? 'text-gray-800' : 'text-gray-400'}`}>Sample Selection</span>
          {popReady && (
            <span className="ml-auto text-xs font-semibold bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
              {popUploadForSampling.selectedSamples.length} selected
            </span>
          )}
        </div>

        {!ipeVerified && (
          <div className="flex items-center gap-2 pl-7">
            <AlertTriangle className="h-3.5 w-3.5 text-gray-300" />
            <p className="text-xs text-gray-400">Complete IPE tie-out (Section C) to unlock sampling.</p>
          </div>
        )}

        {ipeVerified && (
          <div className="border border-gray-200 rounded-xl p-4 space-y-3">
            <p className="text-xs text-gray-600">
              Population verified: <strong>{popUpload.result?.total_count?.toLocaleString()}</strong> records.
              Select your samples below.
            </p>
            <PopulationFilePanel upload={popUploadForSampling} control={control} suppressBanner />
          </div>
        )}
      </div>

      {/* ── Actions ────────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
        <button type="button" onClick={() => {
          const ph = popUploadForSampling.phase;
          if (ph === 'sampled') { popUploadForSampling.setPhase('sampling'); }
          else { onBack(); }
        }} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
          ← Back
        </button>
        <button type="button" onClick={handleSave} disabled={!canSave}
          className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed">
          {canSave
            ? `Save ${popUploadForSampling.selectedSamples.length} sample${popUploadForSampling.selectedSamples.length !== 1 ? 's' : ''} & Finalise →`
            : 'Save Configuration →'}
        </button>
        {!canSave && ipeVerified && !popReady && (
          <p className="text-xs text-gray-400">Select at least one sample (Section D) to save.</p>
        )}
        {!canSave && !ipeVerified && (
          <p className="text-xs text-amber-600">Complete IPE verification (Section C) to unlock sampling.</p>
        )}
      </div>
    </div>
  );
}

/* ---- Event-based: population file required ---- */
function EventBasedPopulation({ control, storeCtx, initialPopulation, initialSamplingMethod, onComplete, onBack }) {
  const upload = usePopulationFileUpload(initialPopulation, storeCtx);
  const [ipeTieout, setIpeTieout] = useState({});

  const hasFileSamples = upload.phase === 'sampled' && upload.selectedSamples.length > 0;

  const ipeFNum = parseFloat(ipeTieout.filtered);
  const ipeINum = parseFloat(ipeTieout.independent);
  const ipeBothFilled = hasFileSamples && !isNaN(ipeFNum) && !isNaN(ipeINum) && ipeTieout.independent !== '';
  const ipeDelta = ipeBothFilled ? Math.abs(ipeFNum - ipeINum) : null;
  const ipeTolerance = ipeBothFilled ? Math.max(1, ipeINum * 0.001) : null;
  const ipeTiedOut = ipeBothFilled && ipeDelta <= ipeTolerance;

  // Confirm waits for the population file's library save; a second click meanwhile must not save the run twice.
  const [confirming, setConfirming] = useState(false);
  const handleConfirm = async () => {
    if (confirming) return;
    setConfirming(true);
    try { await confirmPopulation(); } finally { setConfirming(false); }
  };
  const confirmPopulation = async () => {
    if (!upload.selectedSamples.length) return;
    // The file's library save may still be running: record the id of the file actually sampled.
    const populationFileId = await upload.waitForStoredUpload();
    onComplete({
      populationData: {
        file_name: upload.parseResult?.identifier_column,
        population_file_id: populationFileId ?? undefined,
        total_count: upload.parseResult?.total_count,
        sampling_pct: upload.samplingPct,
        sample_size: upload.selectedSamples.length,
        selected_samples: upload.selectedSamples,
        items: upload.parseResult?.items,
        item_rows: selectedRowData(upload.parseResult, upload.selectedSamples),
        source: 'file_upload',
        filtered_total_amount: ipeBothFilled ? ipeFNum : undefined,
        independent_total_amount: ipeBothFilled ? ipeINum : undefined,
        ipe_comparison_note: ipeTieout.note || '',
      },
      totalPopulation: upload.parseResult?.total_count,
      sampleSize: upload.selectedSamples.length,
      samplingMethod: upload.selectionMode,
      ipeValidated: ipeTiedOut,
    });
  };

  return (
    <div className="p-5 space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-1">Population & samples</h3>
        <p className="text-xs text-gray-500">
          {hasFileSamples
            ? `${upload.selectedSamples.length} item${upload.selectedSamples.length !== 1 ? 's' : ''} selected — complete IPE tie-out to save.`
            : 'Upload your population file, then pick which items to test.'}
        </p>
      </div>

      <PopulationFilePanel upload={upload} control={control} />

      {hasFileSamples && (
        <IPETieoutPanel
          totalCount={upload.parseResult?.total_count}
          value={ipeTieout}
          onChange={setIpeTieout}
          currency={upload.parseResult?.currency}
        />
      )}

      <div className="flex items-center gap-3">
        <button type="button" onClick={onBack} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
          ← Back
        </button>
        {hasFileSamples && (
          <button type="button" onClick={handleConfirm} disabled={!ipeTiedOut || confirming}
            className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-40">
            Save {upload.selectedSamples.length} sample{upload.selectedSamples.length !== 1 ? 's' : ''} →
          </button>
        )}
      </div>
      {hasFileSamples && !ipeTiedOut && (
        <p className="text-xs text-amber-600 -mt-2">Complete IPE tie-out (both totals must agree) to save.</p>
      )}
    </div>
  );
}
